import React, { useState, useEffect } from 'react';
import { MapIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline'; // Importing CheckIcon and XMarkIcon

function MyLearningGoals() {
  const [learningGoals, setLearningGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userGrade, setUserGrade] = useState(null);
  const [userRole, setUserRole] = useState(null);

  // Static data for learning goals, now including a 'completed' status
  const allLearningGoals = {
    'beginners': {
      publicSpeaking: {
        title: 'Confidence Building',
        levels: [
          {
            name: 'Level 1: Speaking Start',
            goals: [
              { text: 'I can say my name and talk about myself.', completed: false },
              { text: 'I can speak in full sentences.', completed: false },
              { text: 'I can use expressions while speaking.', completed: false },
            ],
          },
          {
            name: 'Level 2: Stage Ready',
            goals: [
              { text: 'I can make eye contact while speaking.', completed: false },
              { text: 'I can speak clearly in front of a group.', completed: false },
              { text: 'I can tell short stories with actions.', completed: false },
            ],
          },
          {
            name: 'Level 3: Structured Speaker',
            goals: [
              { text: 'I can follow a story order while speaking.', completed: false },
              { text: 'I can use props while speaking.', completed: false },
              { text: 'I can remember 3-4 lines and speak aloud.', completed: false },
            ],
          },
          {
            name: 'Level 4: Impactful Communicator',
            goals: [
              { text: 'I can talk about what I see, feel, and think.', completed: false },
              { text: 'I can describe a picture in my own words.', completed: false },
            ],
          },
          {
            name: 'Level 5: Confident Orator',
            goals: [
              { text: 'I can recite poems or monologues with expressions.', completed: false },
              { text: 'I can introduce my friends or family on stage.', completed: false },
            ],
          },
        ],
      },
      creativeWriting: {
        title: 'Thought Structuring',
        levels: [
          {
            name: 'Level 1: Budding Writer',
            goals: [
              { text: 'I can write simple stories with pictures.', completed: false },
              { text: 'I can describe people and places.', completed: false },
              { text: 'I can use capital letters and punctuation.', completed: false },
            ],
          },
          {
            name: 'Level 2: Expressive Creator',
            goals: [
              { text: 'I can write rhymes and fun poems.', completed: false },
              { text: 'I can describe what I see in a picture.', completed: false },
              { text: 'I can use new words in my writing.', completed: false },
            ],
          },
          {
            name: 'Level 3: Story Builder',
            goals: [
              { text: 'I can write what happened in my day as a story.', completed: false },
              { text: 'I can make up endings to stories I hear.', completed: false },
            ],
          },
          {
            name: 'Level 4: Creative Craftsman',
            goals: [
              { text: 'I can write a story using a given title.', completed: false },
              { text: 'I can illustrate and narrate my stories.', completed: false },
            ],
          },
          {
            name: 'Level 5: Wordsmith & Publisher',
            goals: [
              { text: 'I can make a storybook using my own story.', completed: false },
              { text: 'I can share my writing aloud.', completed: false },
            ],
          },
        ],
      },
    },
    'intermediate': {
      publicSpeaking: {
        title: 'Confidence Building ',
        levels: [
          {
            name: 'Level 1: Speaking Start',
            goals: [
              { text: 'I can speak about myself for 1-2 minutes.', completed: false },
              { text: 'I can use voice variation (pitch, tone).', completed: false },
              { text: 'I can share stories with clear beginning and end.', completed: false },
            ],
          },
          {
            name: 'Level 2: Stage Ready',
            goals: [
              { text: 'I can use gestures and posture correctly.', completed: false },
              { text: 'I can speak on simple topics confidently.', completed: false },
              { text: 'I can handle Q&A with short answers.', completed: false },
            ],
          },
          {
            name: 'Level 3: Structured Speaker',
            goals: [
              { text: 'I can organize a speech (intro, body, conclusion).', completed: false },
              { text: 'I can use pauses and emphasis.', completed: false },
              { text: 'I can speak on current or abstract topics.', completed: false },
            ],
          },
          {
            name: 'Level 4: Impactful Communicator',
            goals: [
              { text: 'I can use anecdotes, facts, and quotes.', completed: false },
              { text: 'I can deliver persuasive speeches.', completed: false },
              { text: 'I can adjust my speech for different audiences.', completed: false },
            ],
          },
          {
            name: 'Level 5: Confident Orator',
            goals: [
              { text: 'I can improvise speeches on-the-spot.', completed: false },
              { text: 'I can participate in debates and storytelling contests.', completed: false },
              { text: 'I can give peer feedback.', completed: false },
            ],
          },
        ],
      },
      creativeWriting: {
        title: 'Thought Structuring ',
        levels: [
          {
            name: 'Level 1: Budding Writer',
            goals: [
              { text: 'I can write complete paragraphs with a clear message.', completed: false },
              { text: 'I can describe using adjectives and sensory details.', completed: false },
              { text: 'I can write creative titles and endings.', completed: false },
            ],
          },
          {
            name: 'Level 2: Expressive Creator',
            goals: [
              { text: 'I can write dialogues between characters.', completed: false },
              { text: 'I can create story plots with conflict.', completed: false },
              { text: 'I can revise my writing with help.', completed: false },
            ],
          },
          {
            name: 'Level 3: Story Builder',
            goals: [
              { text: 'I can use plot structure (beginning, conflict, climax, end).', completed: false },
              { text: 'I can write from different points of view.', completed: false },
              { text: 'I can build suspense or surprise.', completed: false },
            ],
          },
          {
            name: 'Level 4: Creative Craftsman',
            goals: [
              { text: 'I can use similes, metaphors, and vivid imagery.', completed: false },
              { text: 'I can write persuasive essays or speeches.', completed: false },
              { text: 'I can edit my work using peer feedback.', completed: false },
            ],
          },
          {
            name: 'Level 5: Wordsmith & Publisher',
            goals: [
              { text: 'I can publish my work on Oratrics or school.', completed: false },
            ],
          },
        ],
      },
    },
  };

  useEffect(() => {
    const fetchUserAndGoals = async () => {
      setLoading(true);
      setError(null);
      try {
        const userDataString = localStorage.getItem("user");
        if (userDataString) {
          const userData = JSON.parse(userDataString);
          const grade = userData.grade;
          const role = userData.role;
          setUserGrade(grade);
          setUserRole(role);

          let goalsToDisplay = [];
          const parsedGrade = parseInt(grade);

          if (grade && (grade.toLowerCase() === 'kg' || (parsedGrade >= 1 && parsedGrade <= 3))) {
            goalsToDisplay = [
              JSON.parse(JSON.stringify(allLearningGoals.beginners.publicSpeaking)),
              JSON.parse(JSON.stringify(allLearningGoals.beginners.creativeWriting))
            ];
          } else if (grade && (parsedGrade >= 4 && parsedGrade <= 8)) {
            goalsToDisplay = [
              JSON.parse(JSON.stringify(allLearningGoals.intermediate.publicSpeaking)),
              JSON.parse(JSON.stringify(allLearningGoals.intermediate.creativeWriting))
            ];
          } else if (grade && parsedGrade > 8) {
            goalsToDisplay = [
              JSON.parse(JSON.stringify(allLearningGoals.intermediate.publicSpeaking)),
              JSON.parse(JSON.stringify(allLearningGoals.intermediate.creativeWriting))
            ];
          } else {
            setError("User grade not found or invalid format. Cannot display learning goals.");
          }
          setLearningGoals(goalsToDisplay);
        } else {
          setError("User data not found in local storage. Please ensure you are logged in.");
        }
      } catch (err) {
        console.error("Error fetching user data or setting goals:", err);
        setError("Failed to load learning goals. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchUserAndGoals();
  }, []);

  const handleGoalCompletionChange = (subjectIndex, levelIndex, goalIndex, isChecked) => {
    setLearningGoals(prevGoals => {
      const newGoals = [...prevGoals];
      const targetSubject = { ...newGoals[subjectIndex] };
      const targetLevels = [...targetSubject.levels];
      const targetLevel = { ...targetLevels[levelIndex] };
      const targetGoals = [...targetLevel.goals];

      targetGoals[goalIndex] = {
        ...targetGoals[goalIndex],
        completed: isChecked,
      };

      targetLevel.goals = targetGoals;
      targetLevels[levelIndex] = targetLevel;
      targetSubject.levels = targetLevels;
      newGoals[subjectIndex] = targetSubject;

      // --- Future API Integration Point ---
      // In a real application, you would send this update to your backend API here.
      // Example:
      // const goalId = "someUniqueGoalIdentifier"; // You'd need a unique ID for each goal
      // fetch('/api/update-goal-status', {
      //   method: 'POST',
      //   headers: {
      //     'Content-Type': 'application/json',
      //   },
      //   body: JSON.stringify({ goalId, completed: isChecked }),
      // })
      // .then(response => response.json())
      // .then(data => console.log('Goal updated on server:', data))
      // .catch(error => console.error('Error updating goal:', error));
      // ------------------------------------

      return newGoals;
    });
  };

  return (
    <div className="bg-white rounded-3xl p-8 shadow-xl border-b-4 border-pink-600 transition-all duration-300 hover:shadow-2xl hover:scale-[1.005]">
      <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-6 flex items-center">
        <MapIcon className="w-9 h-9 mr-4 text-pink-600" />
        My Learning Goals
      </h2>

      {loading ? (
        <p className="text-gray-600 text-center py-6 text-lg animate-pulse">Fetching your learning path...</p>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg text-center shadow-inner">
          <p className="font-semibold mb-2">Error Loading Goals:</p>
          <p>{error}</p>
          <p className="text-sm mt-2">Please ensure you are logged in and your profile is complete.</p>
        </div>
      ) : (
        <div>
          {learningGoals.length > 0 ? (
            learningGoals.map((subject, subjectIndex) => (
              <div key={subjectIndex} className="mb-10 last:mb-0 bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 shadow-md hover:shadow-lg transition-shadow duration-200">
                <h3 className="text-2xl font-bold text-purple-800 mb-5 border-b-2 border-purple-200 pb-3">
                  {subject.title}
                </h3>
                {subject.levels.map((level, levelIndex) => (
                  <div key={levelIndex} className="bg-white p-5 rounded-xl shadow-sm mb-4 last:mb-0 border border-gray-100">
                    <p className="font-semibold text-lg text-gray-800 mb-3 flex items-center">
                      <span className="text-pink-500 mr-2">✦</span> {level.name}
                    </p>
                    <ul className="list-none pl-0 space-y-3 text-gray-700 text-base">
                      {level.goals.map((goal, goalIndex) => (
                        <li key={goalIndex} className="flex items-start group py-1">
                          {userRole === 'teacher' ? (
                            // Teacher view: interactive checkbox with custom styling
                            <label className="flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={goal.completed}
                                onChange={(e) => handleGoalCompletionChange(subjectIndex, levelIndex, goalIndex, e.target.checked)}
                                className="hidden" // Hide default checkbox
                              />
                              <div className={`
                                flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all duration-200 ease-in-out
                                ${goal.completed
                                  ? 'bg-green-500 border-green-500 text-white'
                                  : 'bg-white border-gray-300 text-transparent group-hover:border-pink-400 group-hover:text-pink-400'
                                }
                              `}>
                                {goal.completed ? (
                                  <CheckIcon className="w-4 h-4" />
                                ) : (
                                  <XMarkIcon className="w-4 h-4 group-hover:opacity-100 opacity-0" /> // Hidden by default, visible on hover
                                )}
                              </div>
                              <span className={`ml-3 transition-colors duration-200 ${goal.completed ? 'line-through text-gray-500 italic' : 'text-gray-800 font-medium'}`}>
                                {goal.text}
                              </span>
                            </label>
                          ) : (
                            // Student view: just the goal text with completion indication
                            <div className="flex items-center">
                               <div className={`
                                w-5 h-5 rounded-full border-2 flex-shrink-0 items-center justify-center mr-3
                                ${goal.completed ? 'bg-green-100 border-green-400' : 'bg-gray-100 border-gray-300'}
                               `}>
                                {goal.completed ? (
                                    <CheckIcon className="w-4 h-4 text-green-600 mx-auto my-auto" />
                                ) : (
                                    // You can add another icon for incomplete goals for students if desired, or just leave it empty
                                    null
                                )}
                               </div>
                              <span className={`${goal.completed ? 'line-through text-gray-500 italic' : 'text-gray-800'}`}>
                                {goal.text}
                              </span>
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            ))
          ) : (
            <p className="text-gray-600 text-center py-6 text-lg">No learning goals available for your grade level at the moment. Please contact support if you believe this is an error.</p>
          )}
        </div>
      )}
    </div>
  );
}

export default MyLearningGoals;